import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSession, logout as logoutRequest, requestOtp, verifyOtp } from '../lib/api/endpoints';
import { ApiError, type SessionInfo } from '../lib/api/types';
import { queryKeys } from '../lib/queryClient';

/**
 * Fonte única de verdade de sessão do owner: `GET /web/session` via TanStack
 * Query. Nunca guarda o token em si (ele só existe como cookie HttpOnly, que o
 * browser já anexa sozinho) — só o resultado JSON da sessão, em memória do
 * QueryClient (não em localStorage/sessionStorage — IMVP-03).
 */

/**
 * O probe de sessão tem TRÊS desfechos, e confundir dois quaisquer produz um bug
 * de produto (§34 do contrato R-UX01):
 *
 *  - `anonymous`: o backend respondeu, e a resposta é "não há sessão" (401). É um
 *    resultado normal, não uma falha. A Home pública vive aqui.
 *  - `authenticated`: o backend respondeu com uma sessão que satisfaz o contrato.
 *  - `infrastructure-failure`: não deu para saber. 5xx, queda de rede, proxy
 *    devolvendo HTML no lugar do JSON, corpo fora do contrato. Qualquer coisa que
 *    não seja uma resposta legível do backend.
 *
 * Antes desta classificação o código tinha os dois erros simétricos possíveis:
 *
 *  1. `throwOnError` só perdoava 401, então um 503 (ou uma falha de rede) era
 *     lançado DURANTE O RENDER do provider. Como o `AuthProvider` envolve o
 *     router inteiro e não há error boundary acima dele, a árvore inteira
 *     desmontava: a Home PÚBLICA, que nem consulta sessão, virava tela branca
 *     por causa de um probe que não lhe dizia respeito.
 *  2. A correção ingênua (`throwOnError: false`) trocaria esse bug pelo oposto:
 *     sem sessão legível, `isAuthenticated` cairia para `false` e o `RequireAuth`
 *     mandaria o usuário para `/login`, afirmando "você não está autenticado"
 *     quando a verdade é "o backend não respondeu". Isso mascara incidente de
 *     infraestrutura como estado de usuário, que é justamente o que o contrato
 *     proíbe.
 *
 * Por isso o estado é explícito: a Home nunca quebra, e quem depende de sessão
 * distingue "faça login" de "está fora do ar".
 */
export type SessionStatus = 'loading' | 'anonymous' | 'authenticated' | 'infrastructure-failure';

interface AuthContextValue {
  session: SessionInfo | null;
  status: SessionStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** O erro real por trás de `infrastructure-failure`, para diagnóstico na tela.
   * `null` em qualquer outro estado — inclusive em `anonymous`, porque o 401 que
   * o produziu não é uma falha a exibir. */
  sessionFailure: Error | null;
  /** Repete o probe. Existe para a tela de falha oferecer "tentar de novo" sem
   * exigir reload (que perderia o destino pretendido guardado pelo router). */
  retrySession: () => void;
  requestOtp: (email: string) => Promise<{ verificationHandle: string }>;
  verifyOtp: (input: { verificationHandle: string; code: string }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** 401 é a única resposta que significa "não há sessão". Todo o resto — status
 * inesperado, rede caída, corpo que não é o contrato — é ausência de informação,
 * não ausência de sessão. */
function isAnonymousSignal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: queryKeys.session,
    queryFn: getSession,
    retry: false,
    // Nunca lança no render: o provider envolve rotas públicas, e derrubar a
    // árvore por causa do probe é o bug que esta classificação existe para matar.
    // O erro não é engolido — vira `infrastructure-failure` + `sessionFailure`.
    throwOnError: false,
  });

  const status = useMemo<SessionStatus>(() => {
    if (sessionQuery.status === 'pending') return 'loading';
    if (sessionQuery.status === 'success') {
      // `logout` grava `null` aqui de propósito (sucesso com corpo vazio), e
      // isso é anonimato, não sessão válida.
      return sessionQuery.data ? 'authenticated' : 'anonymous';
    }
    return isAnonymousSignal(sessionQuery.error) ? 'anonymous' : 'infrastructure-failure';
  }, [sessionQuery.status, sessionQuery.data, sessionQuery.error]);

  const requestOtpMutation = useMutation({
    mutationFn: (email: string) => requestOtp({ email }),
  });

  const verifyOtpMutation = useMutation({
    mutationFn: (input: { verificationHandle: string; code: string }) =>
      verifyOtp({ verification_handle: input.verificationHandle, code: input.code }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.session }),
  });

  const logoutMutation = useMutation({
    mutationFn: logoutRequest,
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.session, null);
      queryClient.clear();
    },
  });

  const doRequestOtp = useCallback(
    async (email: string) => {
      const result = await requestOtpMutation.mutateAsync(email);
      return { verificationHandle: result.verificationHandle };
    },
    [requestOtpMutation],
  );

  const doVerifyOtp = useCallback(
    async (input: { verificationHandle: string; code: string }) => {
      await verifyOtpMutation.mutateAsync(input);
    },
    [verifyOtpMutation],
  );

  const doLogout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const doRetrySession = useCallback(() => {
    void queryClient.refetchQueries({ queryKey: queryKeys.session });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session: sessionQuery.data ?? null,
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      sessionFailure:
        status === 'infrastructure-failure' && sessionQuery.error instanceof Error
          ? sessionQuery.error
          : null,
      retrySession: doRetrySession,
      requestOtp: doRequestOtp,
      verifyOtp: doVerifyOtp,
      logout: doLogout,
    }),
    [sessionQuery.data, sessionQuery.error, status, doRetrySession, doRequestOtp, doVerifyOtp, doLogout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}
